<template>
	<div class="store-list-container">
		<div class="header">
			<h1>{{ $t('storeList.title') }}</h1>
			<select v-model="selectedCity" class="city-selector">
				<option value="">{{ $t('storeList.selectCity') }}</option>
				<option v-for="city in cities" :key="city" :value="city">{{ city }}</option>
			</select>
		</div>
		<div class="store-list">
			<div v-for="(store, index) in paginatedStores" :key="index" class="store-item">
				<img :src="store.imageUrl" :alt="store.name" class="store-image" />

				<div class="store-content">
					<h2>{{ store.name }}</h2>
					<div class="tags-container">
						<span v-for="(tag, tagIndex) in store.tags.slice(0, 3)" :key="tagIndex" class="tag">
							{{ tag }}
						</span>
					</div>
					<p>{{ store.businessHours }}</p>
					<p>{{ store.phone }}</p>
					<p>{{ store.address }}</p>
					<button v-if="store.longitude && store.latitude" class="position-button" @click="handleLocationClick(store)">
						<svg width="20" height="20" viewBox="0 0 24 24" class="location-icon">
							<path
								d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
								fill="#007AFF"
							/>
						</svg>
					</button>
				</div>
			</div>
		</div>
		<div class="pagination" v-if="totalPages > 1">
			<button @click="prevPage" :disabled="currentPage === 1" class="page-button">
				{{ $t('storeList.prevPage') }}
			</button>
			<span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
			<button @click="nextPage" :disabled="currentPage === totalPages" class="page-button">
				{{ $t('storeList.nextPage') }}
			</button>
		</div>
	</div>
</template>

<script>
import { brandStoreApi, baseImgUrl } from '../netTool/api';
import { emitEvent } from '../netTool/eventBus.js';

export default {
	data() {
		return {
			stores: [],
			selectedCity: '',
			currentPage: 1,
			pageSize: 8,
			baseImgUrl: baseImgUrl
		};
	},
	computed: {
		cities() {
			const citySet = new Set();
			this.stores.forEach((store) => {
				if (store.city) {
					citySet.add(store.city);
				}
			});
			return Array.from(citySet);
		},
		filteredStores() {
			if (!this.selectedCity) return this.stores;
			return this.stores.filter((store) => store.city === this.selectedCity);
		},
		paginatedStores() {
			const start = (this.currentPage - 1) * this.pageSize;
			const end = start + this.pageSize;
			return this.filteredStores.slice(start, end);
		},
		totalPages() {
			return Math.ceil(this.filteredStores.length / this.pageSize) || 1;
		}
	},
	watch: {
		selectedCity() {
			this.currentPage = 1;
		},
		currentPage(newVal) {
			if (newVal < 1) this.currentPage = 1;
			if (newVal > this.totalPages) this.currentPage = this.totalPages;
		}
	},
	created() {
		this.getStores();
	},
	methods: {
		async getStores() {
			try {
				const temp = await brandStoreApi.getBrandStores();

				if (temp && Array.isArray(temp.data)) {
					this.stores = temp.data.map((store) => ({
						id: store.id,
						name: store.store_name,
						tags: store.store_tags ? store.store_tags.split(/[;；,]/) : [],
						businessHours: store.business_hours,
						phone: store.phone,
						address: store.address,
						city: store.city || '未知城市',
						imageUrl: store.store_image
							? store.store_image.trim()
								? `${baseImgUrl}${store.store_image}`
								: require('@/assets/brandDefault.png')
							: require('@/assets/brandDefault.png'),
						longitude: store.longitude,
						latitude: store.latitude
					}));

					const storesWithLocation = this.stores.filter((store) => store.longitude && store.latitude);

					sessionStorage.setItem('brandstore', JSON.stringify(storesWithLocation));
					emitEvent('stores-updated', storesWithLocation);
				} else {
					this.stores = [];
				}
			} catch (error) {
				this.stores = [];
			}
		},
		prevPage() {
			if (this.currentPage > 1) this.currentPage--;
		},
		nextPage() {
			if (this.currentPage < this.totalPages) this.currentPage++;
		},
		handleLocationClick(store) {
			emitEvent('stores-click', store.id);
		}
	}
};
</script>

<style scoped>
.store-list-container {
	max-width: 1200px;
	margin: 0 auto;
	margin-top: 25px;
	margin-bottom: 40px;
	padding: 0 25px;
}

@media (max-width: 468px) {
	.store-list-container {
		padding: 0 15px;
	}
}

.header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 25px;
}

.city-selector {
	background-color: #fff;
	height: 44px;
	border-radius: 22px;
	padding: 5px;
	text-align: center;
}

.store-list {
	max-width: 1200px;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	grid-gap: 20px;
}

.store-item {
	background-color: #fff;
	border-radius: 15px;
}

.store-content {
	position: relative;
	padding: 22px;
}

.store-content * {
	margin-bottom: 8px;
}

.store-image {
	width: 100%;
	height: auto;
}

.position-button {
	position: absolute;
	right: 12px;
	bottom: 8px;

	width: 32px;
	height: 32px;
	border-radius: 16px;
	background-color: #d5e4ff;
	border: none;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	cursor: pointer;
	transition: all 0.2s ease;
}

.position-button:hover {
	background-color: #c0d6ff;
}

.position-button:active {
	transform: scale(0.95);
}

.location-icon {
	width: 18px; /* 适当放大图标 */
	height: 18px;
	margin: 0; /* 确保没有额外边距 */
}

/* 添加分页样式 */
.pagination {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 20px;
	margin: 20px 0;
	padding: 10px;
}

.page-button {
	padding: 8px 16px;
	border: 1px solid #ddd;
	border-radius: 4px;
	background-color: #fff;
	cursor: pointer;
}

.page-button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.page-info {
	font-size: 14px;
	color: #666;
}

.tags-container {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 8px 0;
}

.tag {
	display: inline-block;
	background-color: #f0f0f0;
	color: #333;
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 12px;
}

</style>
